/**
 * Stage Plot Types & Constants
 *
 * Single source of truth for the stage plot data model.
 * Positions (x/y) are stored in STAGE UNITS (meters or feet) so that
 * resizing the stage keeps element positions consistent.
 * Width/height are DISPLAY pixels (visual size, independent of zoom).
 */

export type PaletteCategory =
  | 'drums'
  | 'percussion'
  | 'bass'
  | 'guitar'
  | 'horns'
  | 'strings'
  | 'keys'
  | 'vocals'
  | 'stage'
  | 'tech';

export type StageUnit = 'm' | 'ft';

export interface StageDimensions {
  /** Stage width in stage units */
  width: number;
  /** Stage depth in stage units */
  height: number;
  unit: StageUnit;
}

export const DEFAULT_STAGE_DIMENSIONS: StageDimensions = {
  width: 12,
  height: 8,
  unit: 'm',
};

export const STAGEPLOT_SCHEMA_VERSION = 2;

/** Pixels per stage unit in the editor canvas. */
export const STAGE_SCALE = 50;

/** Default fill colour per category (used for newly placed items). */
export const CATEGORY_COLORS: Record<PaletteCategory, string> = {
  drums: '#4A90D9',
  percussion: '#50B86C',
  bass: '#9B59B6',
  guitar: '#E67E22',
  horns: '#E74C3C',
  strings: '#1ABC9C',
  keys: '#E91E63',
  vocals: '#F1C40F',
  stage: '#7F8C8D',
  tech: '#16A085',
};

export const PALETTE_CATEGORIES: Record<PaletteCategory, string> = {
  stage: 'Stage',
  drums: 'Drums',
  percussion: 'Percussion',
  bass: 'Bass',
  guitar: 'Guitar',
  horns: 'Horns',
  strings: 'Strings',
  keys: 'Keys',
  vocals: 'Vocals',
  tech: 'Tech / Computers',
};

export interface StageElement {
  id: string;
  /** Key into the SVG icon bank (StageIcons). */
  iconId: string;
  category: PaletteCategory;
  /** Display label shown under / on the element. */
  label: string;
  /** Whether the label text is rendered (shown under the icon in the plot
   * image). Default true; false hides the text entirely. */
  showLabel?: boolean;
  /** Position from stage top-left, in stage units. */
  x: number;
  y: number;
  /** Visual size in display pixels. */
  width: number;
  height: number;
  /** Rotation in degrees (clockwise). */
  rotation: number;
  /** Fill colour (overrides category default). */
  color: string;
  /** Stacking order on the stage (higher = front). Default 0. */
  z?: number;
  /** Mirror horizontally (symmetry / reverse). */
  flipX?: boolean;
  /**
   * Optional structured config for composite elements (e.g. a Drum Kit).
   * Serialised with the plot; consumers cast by iconId.
   */
  config?: Record<string, unknown>;
}

export interface StagePlotMetadata {
  bandName: string;
  venue: string;
  date: string;
  notes: string;
  created: number;
  lastModified: number;
  schemaVersion: number;
}

export interface StagePlot {
  id: string;
  name: string;
  /** `canvas` = editable SVG (stageDimensions + elements); `image` = an
   * imported picture (many bands already have their stage plot as an image).
   * Defaults to `canvas`. (2026-08-11) */
  kind?: 'canvas' | 'image';
  stageDimensions: StageDimensions;
  elements: StageElement[];
  /** For `kind: 'image'`: a PNG/JPEG data URL of the imported stage plot. */
  imageUrl?: string;
  metadata: StagePlotMetadata;
}

export function createElementId(): string {
  return `el-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 7)}`;
}

export function createStagePlotId(): string {
  return `plot-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 7)}`;
}

export function createEmptyStagePlot(name = 'Stage Plot'): StagePlot {
  const now = Date.now();
  return {
    id: createStagePlotId(),
    name,
    stageDimensions: { ...DEFAULT_STAGE_DIMENSIONS },
    elements: [],
    metadata: {
      bandName: '',
      venue: '',
      // Never seeded: the date is not rendered on the stage plot image. The
      // field remains on the type for backward compatibility of stored plots.
      date: '',
      notes: '',
      created: now,
      lastModified: now,
      schemaVersion: STAGEPLOT_SCHEMA_VERSION,
    },
  };
}

// ──────────────────────────────────────────
// Palette definitions
// ──────────────────────────────────────────

export interface PaletteItem {
  iconId: string;
  label: string;
  category: PaletteCategory;
  defaultWidth: number;
  defaultHeight: number;
}

export const PALETTE_ITEMS: PaletteItem[] = [
  // ===== DRUMS =====
  // The assembled "Drum Kit" palette entry (iconId 'drumkit') was REMOVED
  // (2026-10-06): the composite kit editor was judged not optimal, and the
  // separate pieces below (Bass Drum, Snare, Rack Tom, Floor Tom, Hi-Hat,
  // cymbals…) already let a drummer build a kit piece by piece. A LEGACY plot
  // that still contains a 'drumkit' element keeps rendering exactly as before
  // (drumKitToSvg / DrumKitGraphic remain wired for backward compatibility) —
  // only the palette entry and its editor are gone.
  //
  // Drum carpet: 2 × 2 m footprint. The StagePlotPro asset
  // (drum/drummat.png) is 152×129 (aspect ≈ 1.178) and is rendered with
  // object-fit: contain, so inside this SQUARE 2×2 m box the mat is fitted by
  // width (2 m × ~1.70 m) and leaves a small letterbox (~15 cm top and bottom).
  // The image is never stretched and the box stays exactly 2×2 m as requested.
  { iconId: 'drum-mat', label: 'Drum Carpet', category: 'drums', defaultWidth: 2, defaultHeight: 2 },
  // Separate drum pieces (top-down / profile photo assets) — added 2026-09-04
  // so a drummer can place separate real-looking pieces (e.g. a spare kick, a
  // second floor tom) instead of always dropping the full composite kit.
  { iconId: 'kick', label: 'Bass Drum', category: 'drums', defaultWidth: 1.1, defaultHeight: 1.0 },
  { iconId: 'snare', label: 'Snare Drum', category: 'drums', defaultWidth: 0.8, defaultHeight: 1.1 },
  { iconId: 'racktom', label: 'Rack Tom', category: 'drums', defaultWidth: 0.9, defaultHeight: 0.86 },
  { iconId: 'floortom', label: 'Floor Tom', category: 'drums', defaultWidth: 0.9, defaultHeight: 1.16 },
  // Two rack toms clamped to a SINGLE stand (drawn as an SVG bank glyph — no
  // StagePlotPro asset exists). Footprint ~1.1 × 0.9 m: two small toms
  // side-by-side plus the shared tripod base read slightly wider than one
  // isolated rack tom (0.9 m) but not as wide as two independent toms.
  { iconId: 'rack-toms-stand', label: '2 Rack Toms on Stand', category: 'drums', defaultWidth: 1.1, defaultHeight: 0.9 },
  { iconId: 'hihat', label: 'Hi-Hat', category: 'drums', defaultWidth: 0.86, defaultHeight: 1.0 },
  { iconId: 'spd', label: 'SPD Pad', category: 'drums', defaultWidth: 1.2, defaultHeight: 1.6 },
  { iconId: 'ride', label: 'Ride Cymbal', category: 'drums', defaultWidth: 1.12, defaultHeight: 1.12 },
  { iconId: 'crash', label: 'Crash Cymbal', category: 'drums', defaultWidth: 1.12, defaultHeight: 1.12 },
  { iconId: 'cymbal', label: 'Cymbal', category: 'drums', defaultWidth: 1.12, defaultHeight: 1.12 },
  // Cymbals added 2026-10-06 on user request: "des cymbales splash et china,
  // ainsi qu'une vue de dessus des cymbales (sans voir le pied)".
  // ride/crash/cymbal above are the historical StagePlotPro photos: ride and
  // crush carry a FULL TRIPOD, and the 'cymbal' photo is top-down but its stand
  // legs are still drawn. 'cymbal-top' is therefore the SAME StagePlotPro
  // cymbal photo CROPPED to the disc alone (rows 0..45 of the 46x63 source =
  // a perfect 46x46 circle) so no stand is visible — while the untouched
  // 'cymbal' entry keeps rendering existing plots exactly as before.
  // splash & china have NO StagePlotPro asset and are TOP-DOWN bank glyphs, so
  // the whole plan view stays coherent (china reads as an ANGULAR disc, the
  // tell-tale of its flanged rim).
  { iconId: 'cymbal-top', label: 'Cymbal (Top View)', category: 'drums', defaultWidth: 0.9, defaultHeight: 0.9 },
  { iconId: 'splash', label: 'Splash Cymbal', category: 'drums', defaultWidth: 0.6, defaultHeight: 0.6 },
  { iconId: 'china', label: 'China Cymbal', category: 'drums', defaultWidth: 0.95, defaultHeight: 0.95 },
  { iconId: 'stool', label: 'Drum Stool', category: 'drums', defaultWidth: 0.68, defaultHeight: 0.68 },
  { iconId: 'hardware', label: 'Drum Hardware', category: 'drums', defaultWidth: 0.84, defaultHeight: 0.84 },

  // ===== PERCUSSION =====
  { iconId: 'bongos', label: 'Bongos', category: 'percussion', defaultWidth: 1.0, defaultHeight: 1.0 },
  { iconId: 'congas', label: 'Congas', category: 'percussion', defaultWidth: 1.12, defaultHeight: 1.12 },
  { iconId: 'timbales', label: 'Timbales', category: 'percussion', defaultWidth: 1.16, defaultHeight: 1.0 },
  { iconId: 'gong', label: 'Gong', category: 'percussion', defaultWidth: 1.28, defaultHeight: 1.28 },
  { iconId: 'shaker', label: 'Shaker', category: 'percussion', defaultWidth: 0.48, defaultHeight: 0.8 },
  { iconId: 'tambourine', label: 'Tambourine', category: 'percussion', defaultWidth: 0.8, defaultHeight: 0.8 },
  { iconId: 'triangle', label: 'Triangle', category: 'percussion', defaultWidth: 0.72, defaultHeight: 0.72 },
  { iconId: 'timpani', label: 'Timpani', category: 'percussion', defaultWidth: 1.16, defaultHeight: 1.16 },

  // ===== BASS =====
  { iconId: 'bass-guitar', label: 'Bass Guitar', category: 'bass', defaultWidth: 1.0, defaultHeight: 1.28 },
  { iconId: 'upright-bass', label: 'Upright Bass', category: 'bass', defaultWidth: 0.88, defaultHeight: 1.36 },
  { iconId: 'bass-amp', label: 'Bass Amp', category: 'bass', defaultWidth: 1.28, defaultHeight: 1.12 },
  { iconId: 'bass-cab', label: 'Bass Cab', category: 'bass', defaultWidth: 1.28, defaultHeight: 1.12 },
  { iconId: 'bass-di', label: 'Bass DI', category: 'bass', defaultWidth: 0.6, defaultHeight: 0.44 },

  // ===== GUITAR =====
  { iconId: 'e-guitar', label: 'Electric Guitar', category: 'guitar', defaultWidth: 1.0, defaultHeight: 1.28 },
  { iconId: 'a-guitar', label: 'Acoustic Guitar', category: 'guitar', defaultWidth: 1.0, defaultHeight: 1.32 },
  { iconId: 'guitar-amp', label: 'Guitar Amp', category: 'guitar', defaultWidth: 1.16, defaultHeight: 1.04 },
  { iconId: 'guitar-cab', label: 'Guitar Cab', category: 'guitar', defaultWidth: 1.16, defaultHeight: 1.04 },
  { iconId: 'guitar-di', label: 'Guitar DI', category: 'guitar', defaultWidth: 0.6, defaultHeight: 0.44 },
  // Empty guitar stands (StagePlotPro gt_stand assets). Defaults follow the
  // assets' own aspect (guitarstand 50x53, doublestand 55x75) so the photo is
  // not letterboxed inside its box. The triple stand asset also exists but is
  // NOT exposed: a double + duplication covers it, and it would add a third
  // near-identical entry.
  { iconId: 'guitar-stand', label: 'Guitar Stand', category: 'guitar', defaultWidth: 0.85, defaultHeight: 0.90 },
  { iconId: 'guitar-stand-double', label: '2-Guitar Stand', category: 'guitar', defaultWidth: 0.8, defaultHeight: 1.09 },

  // ===== HORNS =====
  { iconId: 'trumpet', label: 'Trumpet', category: 'horns', defaultWidth: 0.8, defaultHeight: 0.6 },
  { iconId: 'sax', label: 'Saxophone', category: 'horns', defaultWidth: 0.84, defaultHeight: 0.88 },
  { iconId: 'trombone', label: 'Trombone', category: 'horns', defaultWidth: 0.96, defaultHeight: 0.6 },
  { iconId: 'frenchhorn', label: 'French Horn', category: 'horns', defaultWidth: 0.88, defaultHeight: 0.8 },
  { iconId: 'flute', label: 'Flute', category: 'horns', defaultWidth: 0.68, defaultHeight: 0.56 },
  { iconId: 'clarinet', label: 'Clarinet', category: 'horns', defaultWidth: 0.64, defaultHeight: 0.8 },

  // ===== STRINGS =====
  { iconId: 'violin', label: 'Violin', category: 'strings', defaultWidth: 0.72, defaultHeight: 1.12 },
  { iconId: 'viola', label: 'Viola', category: 'strings', defaultWidth: 0.76, defaultHeight: 1.16 },
  { iconId: 'cello', label: 'Cello', category: 'strings', defaultWidth: 0.84, defaultHeight: 1.28 },
  { iconId: 'doublebass', label: 'Double Bass', category: 'strings', defaultWidth: 0.92, defaultHeight: 1.36 },
  { iconId: 'harp', label: 'Harp', category: 'strings', defaultWidth: 0.96, defaultHeight: 1.28 },

  // ===== KEYS =====
  { iconId: 'stage-piano', label: 'Stage Piano', category: 'keys', defaultWidth: 1.68, defaultHeight: 0.6 },
  { iconId: 'grand-piano', label: 'Grand Piano', category: 'keys', defaultWidth: 1.6, defaultHeight: 1.4 },
  { iconId: 'upright-piano', label: 'Upright Piano', category: 'keys', defaultWidth: 1.5, defaultHeight: 0.7 },
  { iconId: 'synth', label: 'Synthesizer', category: 'keys', defaultWidth: 1.48, defaultHeight: 0.52 },
  { iconId: 'organ', label: 'Organ', category: 'keys', defaultWidth: 1.48, defaultHeight: 0.64 },
  { iconId: 'keytar', label: 'Keytar', category: 'keys', defaultWidth: 1.28, defaultHeight: 0.52 },
  { iconId: 'keystand', label: 'Keyboard Stand', category: 'keys', defaultWidth: 1.64, defaultHeight: 0.56 },
  // Second EMPTY synth/keyboard stand (StagePlotPro keyborad/ApexStand): the
  // A-frame single-column stand, whose footprint is the REVERSE proportion of
  // the X-braced 'keystand' above (taller than wide) — a distinct silhouette.
  { iconId: 'keystand-apex', label: 'Keyboard Stand (Apex)', category: 'keys', defaultWidth: 1.15, defaultHeight: 1.05 },

  // ===== VOCALS =====
  { iconId: 'vocal-mic', label: 'Vocal Mic', category: 'vocals', defaultWidth: 0.52, defaultHeight: 0.8 },
  { iconId: 'condenser', label: 'Condenser Mic', category: 'vocals', defaultWidth: 0.52, defaultHeight: 0.8 },
  { iconId: 'ribbon', label: 'Ribbon Mic', category: 'vocals', defaultWidth: 0.52, defaultHeight: 0.8 },
  { iconId: 'singer', label: 'Singer', category: 'vocals', defaultWidth: 0.88, defaultHeight: 1.12 },
  { iconId: 'iem', label: 'In-Ear Monitor', category: 'vocals', defaultWidth: 0.8, defaultHeight: 0.6 },
  { iconId: 'headphones', label: 'Headphones', category: 'vocals', defaultWidth: 0.8, defaultHeight: 0.6 },

  // ===== STAGE =====
{ iconId: 'risers', label: 'Risers', category: 'stage', defaultWidth: 1, defaultHeight: 2 },
  { iconId: 'wedge', label: 'Wedge Monitor', category: 'stage', defaultWidth: 1.04, defaultHeight: 0.68 },
  { iconId: 'sidefill', label: 'Sidefill', category: 'stage', defaultWidth: 1.12, defaultHeight: 1.2 },
  { iconId: 'monitor', label: 'Monitor Console', category: 'stage', defaultWidth: 1.2, defaultHeight: 0.64 },
  { iconId: 'sub', label: 'Subwoofer', category: 'stage', defaultWidth: 1.12, defaultHeight: 1.12 },
  { iconId: 'linearray', label: 'Line Array', category: 'stage', defaultWidth: 1.0, defaultHeight: 1.48 },
  { iconId: 'paspeaker', label: 'PA Speaker', category: 'stage', defaultWidth: 1.08, defaultHeight: 1.2 },
  { iconId: 'outlet', label: 'Power Outlet', category: 'stage', defaultWidth: 0.44, defaultHeight: 0.44 },
  { iconId: 'distro', label: 'Power Distro', category: 'stage', defaultWidth: 0.84, defaultHeight: 0.52 },
  { iconId: 'generator', label: 'Generator', category: 'stage', defaultWidth: 1.12, defaultHeight: 0.92 },
  { iconId: 'truss', label: 'Truss', category: 'stage', defaultWidth: 2.4, defaultHeight: 0.36 },
  // Racks / flight cases — same 'stage' category as truss / distro / generator.
  // The mirror variants (RackL/R, flightcaseL/R) are DELIBERATELY not exposed:
  // the editor already mirrors any element (flipX / ⇋ Reverse), so the L/R
  // assets would only duplicate a render the user can produce with one click.
  { iconId: 'rack', label: 'Equipment Rack', category: 'stage', defaultWidth: 0.6, defaultHeight: 0.66 },
  { iconId: 'flightcase', label: 'Flight Case', category: 'stage', defaultWidth: 1.0, defaultHeight: 0.82 },
  { iconId: 'light-fixture', label: 'Light Fixture', category: 'stage', defaultWidth: 0.68, defaultHeight: 0.84 },
  { iconId: 'par', label: 'PAR Can', category: 'stage', defaultWidth: 0.64, defaultHeight: 0.8 },
  { iconId: 'movinghead', label: 'Moving Head', category: 'stage', defaultWidth: 0.72, defaultHeight: 0.88 },
  { iconId: 'micstand', label: 'Mic Stand', category: 'stage', defaultWidth: 0.48, defaultHeight: 1.2 },
  // Mic-stand variants (all thin & tall StagePlotPro assets): each default box
  // keeps the ~1.2 m height and a width matching the asset's own aspect ratio
  // so the 'contain' render fills the box without distortion. The editor ALSO
  // supports mirroring (flipX / ⇋ Reverse), so left/right instrument stands are
  // exposed as two entries only because the two photos genuinely differ (boom
  // angle), never because mirroring is unavailable.
  { iconId: 'mic-stand-straight', label: 'Mic Stand (No Mic)', category: 'stage', defaultWidth: 0.88, defaultHeight: 1.2 },
  { iconId: 'mic-stand-vocal', label: 'Vocal Mic Stand', category: 'stage', defaultWidth: 0.52, defaultHeight: 1.2 },
  { iconId: 'mic-stand-boom', label: 'Boom Mic Stand', category: 'stage', defaultWidth: 0.63, defaultHeight: 1.2 },
  { iconId: 'mic-drum-overhead', label: 'Drum Overhead Mic', category: 'stage', defaultWidth: 0.44, defaultHeight: 1.2 },
  { iconId: 'mic-instrument-left', label: 'Instrument Mic (Left)', category: 'stage', defaultWidth: 0.55, defaultHeight: 1.2 },
  { iconId: 'mic-instrument-right', label: 'Instrument Mic (Right)', category: 'stage', defaultWidth: 0.55, defaultHeight: 1.2 },
  { iconId: 'dibox', label: 'DI Box', category: 'stage', defaultWidth: 0.6, defaultHeight: 0.44 },
  { iconId: 'cable', label: 'Cable', category: 'stage', defaultWidth: 1.2, defaultHeight: 0.28 },
  { iconId: 'chair', label: 'Chair (no arms)', category: 'stage', defaultWidth: 0.5, defaultHeight: 0.5 },

  // ===== TECH / COMPUTERS =====
  { iconId: 'laptop', label: 'Laptop', category: 'tech', defaultWidth: 1.12, defaultHeight: 1.12 },
  { iconId: 'desktop', label: 'Desktop Computer', category: 'tech', defaultWidth: 0.88, defaultHeight: 1.28 },
  { iconId: 'drum-machine', label: 'Drum Machine', category: 'tech', defaultWidth: 1.28, defaultHeight: 0.96 },
  { iconId: 'mpc', label: 'MPC / Sampler', category: 'tech', defaultWidth: 0.96, defaultHeight: 1.12 },
  { iconId: 'pad-controller', label: 'Pad Controller', category: 'tech', defaultWidth: 1.0, defaultHeight: 1.1 },
  { iconId: 'groovebox', label: 'Groovebox', category: 'tech', defaultWidth: 1.28, defaultHeight: 1.04 },
  { iconId: 'synth-module', label: 'Synth Module', category: 'tech', defaultWidth: 0.88, defaultHeight: 1.28 },
  { iconId: 'modular-synth', label: 'Modular Synth', category: 'tech', defaultWidth: 1.0, defaultHeight: 1.3 },
  { iconId: 'audio-interface', label: 'Audio Interface', category: 'tech', defaultWidth: 1.28, defaultHeight: 0.84 },
  { iconId: 'midi-keyboard', label: 'MIDI Keyboard', category: 'tech', defaultWidth: 1.28, defaultHeight: 0.88 },
  { iconId: 'tablet', label: 'Tablet', category: 'tech', defaultWidth: 0.8, defaultHeight: 1.12 },
];

/** Build a StageElement from a palette item at a stage-unit position. */
export function createElementFromPalette(
  item: PaletteItem,
  x: number,
  y: number,
  color: string,
): StageElement {
  return {
    id: createElementId(),
    iconId: item.iconId,
    category: item.category,
    label: item.label,
    x,
    y,
    width: item.defaultWidth,
    height: item.defaultHeight,
    rotation: 0,
    color,
  };
}
